<template>
  <section v-if="isVisible" id="RECHARGE_TRANSACTIONS_OVERVIEW_DETAILS" class="offset-bottom-2 scroll_top">
    <VNotification v-if="transactionIds" type="SUCCESS" heading="" class="offset-top-3" closable @close="onCloseNotification">
      <span>{{ $t('rechargingtransactions.multiple_transactions_found') }}</span>
      <span role="list">
        <span v-for="(xid, index) in transactionIds" :key="xid" class="comma-list" role="listitem">
          <span v-if="index !== 0">, </span>
          <span v-if="xid === String(transactionId)">
            <span class="sr-only">{{ $t('common.currently_active') }}</span>
            {{ xid
            }}<!-- no whitespace --></span
          >
          <router-link v-else :to="transactionIdRoute(xid)">{{ xid }}</router-link>
        </span>
      </span>
    </VNotification>
    <div class="tabset-header">
      <h3>{{ $t('rechargingtransactions.details_headline') + ' ' + transactionId }}</h3>
    </div>

    <transition name="fade" mode="out-in" appear>
      <VMessage v-if="msgBoxLoadTransactionDetail.type" v-bind="msgBoxLoadTransactionDetail" class="offset-top-2" style="margin-bottom: 1500px" />
      <div v-else>
        <VSection label="rechargingtransactions.transaction_headline">
          <VDescriptionListDivided>
            <VEntry :data="rechargeDetails" field="transaction.transactionId" name="rechargingtransactions.transaction_id_label" />
            <VEntry
              :value="$t(`rechargingtransactions.method_${rechargeDetails.transaction.rechargingMethod}`)"
              name="rechargingtransactions.transaction_top_up_method_label"
            />
            <VEntry
              :value="$t(`rechargingtransactions.soc_id_${rechargeDetails.transaction.serviceOrderChannelId}`)"
              name="rechargingtransactions.transaction_channel_label"
            />
            <VEntry
              :value="$t(`rechargingtransactions.transaction_state_${rechargeDetails.transaction.transactionStateId}`)"
              name="rechargingtransactions.transaction_state_label"
            />
            <VEntryNumber
              :data="rechargeDetails"
              field="transaction.amount"
              currency-field="transaction.currency"
              name="rechargingtransactions.transaction_top_up_amount_label"
            />
            <VEntry :value="paymentTransactionId" name="rechargingtransactions.paytid_label" />
            <VEntryDate :data="rechargeDetails" field="transaction.start" name="rechargingtransactions.transaction_start_label" />
            <VEntryDate :data="rechargeDetails" field="transaction.topUpTime" name="rechargingtransactions.transaction_top_up_time_label" />
            <VEntryDate :data="rechargeDetails" field="transaction.lastModified" name="rechargingtransactions.transaction_last_change_label" />
            <VEntryDate :data="rechargeDetails" field="transaction.reversalPeriodEnd" name="rechargingtransactions.transaction_reversal_end_label" />
            <VEntry :data="rechargeDetails" field="balanceTopup.relatedParty.0.email" name="rechargingtransactions.top_up_triggered_by_label" />
            <VEntry :data="rechargeDetails" field="transaction.autoTopupId" name="rechargingtransactions.transaction_auto_top_up">
              <template #value="{ value }">
                <router-link v-if="hasAccessAutoTopUpRegistrations" :to="{ name: AUTO_TOPUP_REGISTRATIONS_OVERVIEW_DETAILS, params: { autoTopUpId: value } }">{{
                  value
                }}</router-link>
                <span v-else>{{ value }}</span>
              </template>
            </VEntry>
          </VDescriptionListDivided>
        </VSection>
        <VSection label="rechargingtransactions.payment_method_headline">
          <VDescriptionListDivided>
            <VEntry v-slot="{ value }" :data="rechargeDetails" field="balanceTopup.paymentMethod.name" name="rechargingtransactions.payment_method_name_label">
              {{ $tm('rechargingtransactions.payment_method_name_', value, true) }}
            </VEntry>
            <VEntry v-slot="{ value }" :data="rechargeDetails" field="balanceTopup.paymentMethod.type" name="rechargingtransactions.payment_method_type_label">
              {{ $tm('rechargingtransactions.payment_method_type_', value, true) }}
            </VEntry>
            <VEntry :data="rechargeDetails" field="balanceTopup.paymentMethod.recurring" boolean name="rechargingtransactions.payment_method_recurring_label" />
            <VEntry :data="rechargeDetails" field="balanceTopup.paymentMethod.id" name="rechargingtransactions.payment_method_id_label" />
            <VEntry :data="rechargeDetails" field="balanceTopup.paymentMethod.details.accountQualifier" name-field="balanceTopup.paymentMethod.name">
              <template #name="{ name }">
                {{ $tm('rechargingtransactions.payment_method_label_', name, true) }}
              </template>
            </VEntry>
            <VEntryDate :data="rechargeDetails" field="balanceTopup.paymentMethod.details.expirationDate" name="rechargingtransactions.expiration_date_label" />
          </VDescriptionListDivided>
        </VSection>
        <VSection label="rechargingtransactions.voucher_headline">
          <VDescriptionListDivided>
            <VEntry :data="rechargeDetails" field="voucher.typeId" name="rechargingtransactions.voucher_provider_id_label" />
            <VEntry :data="rechargeDetails" field="voucher.providerName" name="rechargingtransactions.voucher_provider_name_label" />
            <VEntry :data="rechargeDetails" field="voucher.type" name="rechargingtransactions.voucher_type_label" />
            <VEntry :data="rechargeDetails" field="voucher.serialNumber" name="rechargingtransactions.voucher_serial_number_label" />
          </VDescriptionListDivided>
        </VSection>
        <VSection label="rechargingtransactions.error_headline">
          <VDescriptionListDivided>
            <VEntry :data="rechargeDetails" field="errorInfo.responsibilityName" name="rechargingtransactions.error_responsible_label" />
            <VEntry :data="rechargeDetails" field="errorInfo.errorReason" name="rechargingtransactions.error_reason_label" />
            <VEntry :data="rechargeDetails" field="errorInfo.errorDetail" name="rechargingtransactions.error_detail_label" />
            <VEntry :data="rechargeDetails" field="errorInfo.retryDescription" name="rechargingtransactions.error_repeatable_label" />
          </VDescriptionListDivided>
        </VSection>
        <VSection v-if="!!rechargeDetails.smsInfo && rechargeDetails.smsInfo.length > 0" label="rechargingtransactions.sms_headline">
          <VueGoodTable
            style-class="table table-hover"
            :columns="tableColumnsSms"
            :rows="getSmsRows()"
            :paginate="rechargeDetails.smsInfo.length > 10"
            :rows-per-page-text="$t('common.tablePerPage')"
            :of-text="$t('common.tableOf')"
            :all-text="$t('common.tableAll')"
            :prev-text="$t('common.tablePrev')"
            :next-text="$t('common.tableNext')"
            :line-numbers="false"
          >
            <template #table-column="{ column }">
              <VTableHeader :columns="tableColumnsSms" :column="column" />
            </template>
          </VueGoodTable>
        </VSection>
        <h3 class="intra-header">{{ $t('rechargingtransactions.technical_information_headline') }}</h3>
        <VSection label="rechargingtransactions.partner_contract_information_headline">
          <VDescriptionListDivided>
            <VEntry
              :data="rechargeDetails"
              field="technicalInfo.cashPartner.contractId"
              name="rechargingtransactions.partner_contract_information_cash_partner_id"
            />
            <VEntry
              :data="rechargeDetails"
              field="technicalInfo.cashPartner.name"
              name="rechargingtransactions.partner_contract_information_cash_partner_name"
            />
            <VEntry
              :data="rechargeDetails"
              field="technicalInfo.cashPartner.comment"
              name="rechargingtransactions.partner_contract_information_cash_partner_description"
            />
            <VEntry
              :data="rechargeDetails"
              field="technicalInfo.processor.contractId"
              name="rechargingtransactions.partner_contract_information_processor_id"
            />
            <VEntry :data="rechargeDetails" field="technicalInfo.processor.name" name="rechargingtransactions.partner_contract_information_processor_name" />
          </VDescriptionListDivided>
        </VSection>
        <VSection
          v-if="
            !!rechargeDetails.technicalInfo && !!rechargeDetails.technicalInfo.transportContainer && rechargeDetails.technicalInfo.transportContainer.length > 0
          "
          label="rechargingtransactions.transport_container_headline"
        >
          <VueGoodTable
            style-class="table table-hover"
            :columns="tableColumnsTransportContainer"
            :rows="transportContainerRows"
            :paginate="rechargeDetails.technicalInfo.transportContainer.length > 10"
            :rows-per-page-text="$t('common.tablePerPage')"
            :of-text="$t('common.tableOf')"
            :all-text="$t('common.tableAll')"
            :prev-text="$t('common.tablePrev')"
            :next-text="$t('common.tableNext')"
            :line-numbers="false"
          >
            <template #table-column="{ column }">
              <VTableHeader :columns="tableColumnsTransportContainer" :column="column" />
            </template>
          </VueGoodTable>
        </VSection>
      </div>
    </transition>
  </section>
</template>

<script>
import { mapGetters } from 'vuex';
import VSection from '@/components/Section';
import VDescriptionListDivided from '@/components/DescriptionList/Divided';
import VEntry from '@/components/DescriptionList/Entry';
import VEntryDate from '@/components/DescriptionList/EntryDate';
import VEntryNumber from '@/components/DescriptionList/EntryNumber';
import Proxy from '@/proxies/RechargeTransactionsProxy';
import MessageMixin from '@/mixins/message';
import { VueGoodTable } from 'vue-good-table';
import VTableHeader from '@/components/Table/Header';
import { formatDateTime, formatValue } from '@/utils/date';
import { RECHARGE_TRANSACTIONS_OVERVIEW_DETAILS, AUTO_TOPUP_REGISTRATIONS_OVERVIEW_DETAILS } from '@/router/route-types';

const tableColumnsSms = Object.freeze([
  {
    label: 'rechargingtransactions.sms_sending_time_column_heading',
    tooltip: 'rechargingtransactions.sms_sending_time_tooltip',
    field: 'sendingTime',
    sortable: true,
    filterable: false,
  },
  {
    label: 'rechargingtransactions.sms_direction_column_heading',
    tooltip: 'rechargingtransactions.sms_direction_tooltip',
    field: 'outbound',
    sortable: true,
    filterable: false,
  },
  {
    label: 'rechargingtransactions.sms_message_column_heading',
    tooltip: 'rechargingtransactions.sms_message_tooltip',
    field: 'text',
    sortable: true,
    filterable: false,
  },
  {
    label: 'rechargingtransactions.sms_state_column_heading',
    tooltip: 'rechargingtransactions.sms_state_tooltip',
    field: 'stateId',
    sortable: true,
    filterable: false,
  },
  {
    label: 'rechargingtransactions.sms_error_message_column_heading',
    tooltip: 'rechargingtransactions.sms_error_message_tooltip',
    field: 'errorDescription',
    sortable: true,
    filterable: false,
  },
]);

const tableColumnsTransportContainer = Object.freeze([
  {
    label: 'rechargingtransactions.transport_container_name_column_heading',
    tooltip: 'rechargingtransactions.transport_container_name_tooltip',
    field: 'name',
    sortable: true,
    filterable: false,
    width: '40%',
  },
  {
    label: 'rechargingtransactions.transport_container_description_column_heading',
    tooltip: 'rechargingtransactions.transport_container_description_tooltip',
    field: 'description',
    sortable: true,
    filterable: false,
    width: '40%',
  },
  {
    label: 'rechargingtransactions.transport_container_value_column_heading',
    tooltip: 'rechargingtransactions.transport_container_value_tooltip',
    field: 'value',
    sortable: true,
    filterable: false,
    width: '20%',
  },
]);

export default {
  name: 'RechargeTransactionDetails',

  components: {
    VueGoodTable,
    VSection,
    VDescriptionListDivided,
    VEntry,
    VEntryDate,
    VEntryNumber,
    VMessage: () => import('@/components/Message'),
    VTableHeader,
    VNotification: () => import('@/components/Notification'),
  },

  mixins: [MessageMixin],

  data() {
    return {
      tableColumnsSms,
      tableColumnsTransportContainer,
      transactionId: null,
      rechargeDetails: null,
      msgBoxLoadTransactionDetail: {
        type: '',
        textKey: '',
      },
    };
  },

  computed: {
    isVisible() {
      return this.$route.params.transactionId;
    },

    transportContainerRows() {
      if (!this.rechargeDetails?.technicalInfo?.transportContainer?.length) return [];

      return this.rechargeDetails.technicalInfo.transportContainer.map(row => ({
        name: row.name,
        description: row.description,
        value: formatValue(row.value, 'dateTime'),
      }));
    },

    paymentTransactionId() {
      return this.findTransportContainer('IPS:MPS_paymentTransactionId');
    },

    balanceTotal() {
      return this.findTransportContainer('POA:Balance_Total') / 100;
    },

    transactionIds() {
      const transactionIds = this.$route.params.transactionIds?.split(',');
      if (!transactionIds?.length) {
        return null;
      }
      return transactionIds;
    },

    ...mapGetters('system', ['hasAccessAutoTopUpRegistrations']),
  },

  watch: {
    isVisible: {
      handler: 'onTransactionIdChanged',
      immediate: true,
    },
  },

  created() {
    this.AUTO_TOPUP_REGISTRATIONS_OVERVIEW_DETAILS = AUTO_TOPUP_REGISTRATIONS_OVERVIEW_DETAILS;
  },

  methods: {
    async onTransactionIdChanged(val) {
      const transactionId = parseInt(val, 10);
      if (transactionId === this.transactionId) return;
      this.transactionId = transactionId;
      this.rechargeDetails = null;
      if (!this.transactionId) return;

      this.setCompMessage(this.msgBoxLoadTransactionDetail, 'LOADING');
      try {
        const response = await new Proxy().find(this.transactionId);
        if (response === null) {
          this.setCompMessage(this.msgBoxLoadTransactionDetail, 'ERROR', 'rechargingtransactions.error_found_no_data');
          return;
        }
        this.rechargeDetails = response;

        this.clearCompMessage(this.msgBoxLoadTransactionDetail);

        this.$emit('update', response);
      } catch (error) {
        this.setCompMessage(this.msgBoxLoadTransactionDetail, 'ERROR', 'errorcodes.BACKEND_ERROR');
      }
    },

    getSmsRows() {
      if (this.rechargeDetails === undefined || !this.rechargeDetails.smsInfo || this.rechargeDetails.length < 1) return [];

      return this.rechargeDetails.smsInfo.map(row => ({
        sendingTime: formatDateTime(row.sendingTime),
        outbound:
          typeof row.outbound !== 'boolean'
            ? ' '
            : row.outbound === 0
            ? this.$t('rechargingtransactions.sms_outbound')
            : this.$t('rechargingtransactions.sms_inbound'),
        text: row.text,
        stateId:
          typeof row.stateId !== 'number'
            ? ' '
            : row.stateId === 0
            ? this.$t('rechargingtransactions.sms_state_success')
            : this.$t('rechargingtransactions.sms_state_failure'),
        errorDescription: row.errorDescription,
      }));
    },

    findTransportContainer(name) {
      return this.transportContainerRows.find(obj => obj.name === name)?.value;
    },

    transactionIdRoute(transactionId) {
      return {
        name: RECHARGE_TRANSACTIONS_OVERVIEW_DETAILS,
        params: {
          ...this.$route.params,
          from: null,
          to: null,
          transactionId: transactionId,
          disableScrolling: null,
        },
      };
    },

    onCloseNotification() {
      this.$router.push({
        name: RECHARGE_TRANSACTIONS_OVERVIEW_DETAILS,
        params: {
          ...this.$route.params,
          transactionIds: null,
          disableScrolling: null,
        },
      });
    },
  },
};
</script>

<style scoped lang="scss">
@import '~@scss';

.intra-header {
  font-size: 35px;
}
</style>
